import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DollarSign, CreditCard, Banknote, RefreshCw, X, Check, AlertCircle } from 'lucide-react';
import Modal from '../../ui/Modal';
import { formatMoney } from '../../../utils/formatters';
import './PaymentModal.css';

/**
 * Modal para seleccionar metodo de pago y confirmar venta
 * Soporta pagos mixtos y calculo de comision de tarjeta
 */
// Formatear número como moneda (sin símbolo $)
const formatCurrency = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Parsear string de moneda a número
const parseCurrency = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    // Remover comas y espacios
    const cleaned = value.toString().replace(/,/g, '').replace(/\s/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? '' : num;
};

const PaymentModal = ({
    isOpen,
    onClose,
    onConfirm,
    total,
    totalConComision,
    tienePrecioMayoreo,
    comisionTarjeta = 0,
    cliente,
    creditoDisponible = 0,
    showToast,
    // Opciones
    showCredito = true,
    showEstatusPago = false,
    estatusPago: initialEstatusPago = 'pagado',
    onEstatusPagoChange
}) => {
    const [pagosDetalle, setPagosDetalle] = useState([]);
    const [estatusPago, setEstatusPago] = useState(initialEstatusPago);
    const [metodoRecienAgregado, setMetodoRecienAgregado] = useState(null);
    const [inputValues, setInputValues] = useState({}); // Valores de texto para los inputs
    const inputRefs = useRef({});

    // Metodos de pago disponibles
    const metodosPago = [
        { id: 'efectivo', label: 'Efectivo', icon: Banknote, key: 'E', color: '#16a34a' },
        { id: 'tarjeta_credito', label: 'T. Credito', icon: CreditCard, key: 'C', color: '#2563eb' },
        { id: 'tarjeta_debito', label: 'T. Debito', icon: CreditCard, key: 'D', color: '#7c3aed' },
        { id: 'transferencia', label: 'Transferencia', icon: RefreshCw, key: 'T', color: '#ea580c' }
    ];

    // Agregar credito si esta habilitado y cliente tiene credito
    if (showCredito && cliente?.tiene_credito && creditoDisponible > 0) {
        metodosPago.push({
            id: 'credito',
            label: 'Credito',
            icon: DollarSign,
            key: 'R',
            color: '#dc2626'
        });
    }

    // Resetear estado al abrir
    useEffect(() => {
        if (isOpen) {
            setPagosDetalle([]);
            setEstatusPago(initialEstatusPago);
            setMetodoRecienAgregado(null);
            setInputValues({}); // Limpiar valores de inputs formateados
        }
    }, [isOpen, initialEstatusPago]);

    // Focus y select en el input del método recién agregado
    useEffect(() => {
        if (metodoRecienAgregado && inputRefs.current[metodoRecienAgregado]) {
            setTimeout(() => {
                const input = inputRefs.current[metodoRecienAgregado];
                if (input) {
                    input.focus();
                    input.select();
                }
                setMetodoRecienAgregado(null);
            }, 50);
        }
    }, [metodoRecienAgregado]);

    // Calcular si aplica comision
    const metodosConComision = ['tarjeta_credito', 'tarjeta_debito', 'transferencia'];
    const tienePagoConComision = pagosDetalle.some(p => metodosConComision.includes(p.metodo));
    const aplicaComision = tienePrecioMayoreo && comisionTarjeta > 0 && tienePagoConComision;
    const totalFinal = aplicaComision ? totalConComision : total;

    // Calcular totales
    const totalPagado = pagosDetalle.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
    const restante = Math.round((totalFinal - totalPagado) * 100) / 100;
    const cambio = restante < 0 ? Math.abs(restante) : 0;

    // Calcular monto sugerido
    const calcularMontoSugerido = useCallback((nuevoMetodo) => {
        const tendraComision = tienePrecioMayoreo && comisionTarjeta > 0 &&
            (metodosConComision.includes(nuevoMetodo) || tienePagoConComision);
        const totalAUsar = tendraComision ? totalConComision : total;
        const totalYaPagado = pagosDetalle.reduce((sum, pago) => sum + (parseFloat(pago.monto) || 0), 0);
        return Math.max(0, Math.round((totalAUsar - totalYaPagado) * 100) / 100);
    }, [total, totalConComision, tienePrecioMayoreo, comisionTarjeta, pagosDetalle, tienePagoConComision]);

    // Agregar metodo de pago
    const agregarMetodo = useCallback((metodoId) => {
        if (pagosDetalle.some(p => p.metodo === metodoId)) {
            showToast && showToast('Este metodo de pago ya fue agregado', 'warning');
            return;
        }

        // Validar credito
        if (metodoId === 'credito') {
            if (!cliente?.tiene_credito) {
                showToast && showToast('El cliente no tiene credito activado', 'warning');
                return;
            }
            if (creditoDisponible <= 0) {
                showToast && showToast('El cliente no tiene credito disponible', 'warning');
                return;
            }
        }

        const montoSugerido = calcularMontoSugerido(metodoId);
        setPagosDetalle(prev => [...prev, { metodo: metodoId, monto: montoSugerido }]);
        setMetodoRecienAgregado(metodoId);
    }, [pagosDetalle, cliente, creditoDisponible, calcularMontoSugerido, showToast]);

    // Toggle metodo de pago (agregar o quitar)
    const toggleMetodo = useCallback((metodoId) => {
        const existeIndex = pagosDetalle.findIndex(p => p.metodo === metodoId);

        if (existeIndex !== -1) {
            // Si ya existe, eliminarlo
            setPagosDetalle(prev => prev.filter((_, i) => i !== existeIndex));
            return;
        }

        // Si no existe, agregarlo
        // Validar credito
        if (metodoId === 'credito') {
            if (!cliente?.tiene_credito) {
                showToast && showToast('El cliente no tiene credito activado', 'warning');
                return;
            }
            if (creditoDisponible <= 0) {
                showToast && showToast('El cliente no tiene credito disponible', 'warning');
                return;
            }
        }

        const montoSugerido = calcularMontoSugerido(metodoId);
        setPagosDetalle(prev => [...prev, { metodo: metodoId, monto: montoSugerido }]);
        setMetodoRecienAgregado(metodoId);
    }, [pagosDetalle, cliente, creditoDisponible, calcularMontoSugerido, showToast]);

    // Actualizar monto de un metodo
    const actualizarMonto = (metodoId, valor) => {
        // Guardar el valor de texto tal cual para el input
        setInputValues(prev => ({ ...prev, [metodoId]: valor }));

        // Parsear y guardar el valor numérico
        const nuevoMonto = parseCurrency(valor);
        setPagosDetalle(prev => prev.map(p =>
            p.metodo === metodoId ? { ...p, monto: nuevoMonto === '' ? 0 : nuevoMonto } : p
        ));
    };

    // Formatear al perder foco
    const handleBlur = (metodoId) => {
        const pago = pagosDetalle.find(p => p.metodo === metodoId);
        if (pago) {
            setInputValues(prev => ({ ...prev, [metodoId]: formatCurrency(pago.monto) }));
        }
    };

    // Al enfocar, mostrar solo el número
    const handleFocus = (metodoId, e) => {
        const pago = pagosDetalle.find(p => p.metodo === metodoId);
        if (pago && pago.monto > 0) {
            setInputValues(prev => ({ ...prev, [metodoId]: pago.monto.toString() }));
        }
        e.target.select();
    };

    // Inicializar inputValues cuando se agregan métodos
    useEffect(() => {
        const newInputValues = {};
        pagosDetalle.forEach(pago => {
            if (!(pago.metodo in inputValues)) {
                newInputValues[pago.metodo] = formatCurrency(pago.monto);
            }
        });
        if (Object.keys(newInputValues).length > 0) {
            setInputValues(prev => ({ ...prev, ...newInputValues }));
        }
    }, [pagosDetalle.length]);

    // Eliminar metodo de pago
    const eliminarMetodo = (index) => {
        setPagosDetalle(prev => prev.filter((_, i) => i !== index));
    };

    // Keyboard shortcuts
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            const key = e.key.toUpperCase();

            // Toggle metodos con atajos (agregar o quitar)
            const metodo = metodosPago.find(m => m.key === key);
            if (metodo) {
                e.preventDefault();
                toggleMetodo(metodo.id);
                return;
            }

            // Estatus de pago (si esta habilitado)
            if (showEstatusPago) {
                if (key === 'P') {
                    e.preventDefault();
                    setEstatusPago('pendiente');
                    onEstatusPagoChange && onEstatusPagoChange('pendiente');
                } else if (key === 'G') {
                    e.preventDefault();
                    setEstatusPago('pagado');
                    onEstatusPagoChange && onEstatusPagoChange('pagado');
                }
            }

            // Confirmar con Enter
            if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
            }

            // Cerrar con Escape
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, toggleMetodo, metodosPago, showEstatusPago, onEstatusPagoChange]);

    // Confirmar pago
    const handleConfirm = () => {
        // Validar que el pago sea exacto (con tolerancia de 0.01)
        if (Math.abs(restante) > 0.01 && restante > 0) {
            showToast && showToast(`Falta pagar: ${formatMoney(restante)}`, 'warning');
            return;
        }

        onConfirm({
            pagosDetalle,
            estatusPago,
            totalFinal,
            aplicaComision,
            cambio
        });
    };

    const getMetodoInfo = (metodoId) => {
        return metodosPago.find(m => m.id === metodoId) || {
            label: metodoId,
            icon: DollarSign,
            color: '#64748b'
        };
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Confirmar Pago"
            size="medium"
        >
            <div className="payment-modal-content">
                {/* Totales */}
                <div className="payment-totals">
                    <div className="total-row main-total">
                        <span>Total a pagar:</span>
                        <span className="total-amount">{formatMoney(totalFinal)}</span>
                    </div>
                    {aplicaComision && (
                        <div className="total-row comision-row">
                            <span>
                                <AlertCircle size={14} />
                                Incluye comision ({comisionTarjeta}%)
                            </span>
                            <span>{formatMoney(totalFinal - total)}</span>
                        </div>
                    )}
                </div>

                {/* Metodos de pago disponibles */}
                <div className="payment-methods-section">
                    <label className="section-label">Agregar metodo de pago:</label>
                    <div className="payment-methods-grid">
                        {metodosPago.map(metodo => {
                            const Icon = metodo.icon;
                            const yaAgregado = pagosDetalle.some(p => p.metodo === metodo.id);
                            return (
                                <button
                                    key={metodo.id}
                                    className={`payment-method-btn ${yaAgregado ? 'disabled' : ''}`}
                                    onClick={() => agregarMetodo(metodo.id)}
                                    disabled={yaAgregado}
                                    style={{ '--method-color': metodo.color }}
                                >
                                    <Icon size={18} />
                                    <span className="method-label">{metodo.label}</span>
                                    <span className="method-key">{metodo.key}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Pagos agregados */}
                {pagosDetalle.length > 0 && (
                    <div className="payment-details-section">
                        <label className="section-label">Detalle de pagos:</label>
                        <div className="payment-details-list">
                            {pagosDetalle.map((pago, index) => {
                                const metodoInfo = getMetodoInfo(pago.metodo);
                                const Icon = metodoInfo.icon;
                                return (
                                    <div key={index} className="payment-detail-row">
                                        <div className="detail-method" style={{ '--method-color': metodoInfo.color }}>
                                            <Icon size={16} />
                                            <span>{metodoInfo.label}</span>
                                        </div>
                                        <div className="detail-amount">
                                            <span className="currency-symbol">$</span>
                                            <input
                                                ref={(el) => inputRefs.current[pago.metodo] = el}
                                                type="text"
                                                value={inputValues[pago.metodo] ?? formatCurrency(pago.monto)}
                                                onChange={(e) => actualizarMonto(pago.metodo, e.target.value)}
                                                onFocus={(e) => handleFocus(pago.metodo, e)}
                                                onBlur={() => handleBlur(pago.metodo)}
                                                className="amount-input"
                                                inputMode="decimal"
                                            />
                                        </div>
                                        <button
                                            className="remove-method-btn"
                                            onClick={() => eliminarMetodo(index)}
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Resumen */}
                <div className="payment-summary">
                    <div className="summary-row">
                        <span>Total pagado:</span>
                        <span className={totalPagado >= totalFinal ? 'text-green' : ''}>
                            {formatMoney(totalPagado)}
                        </span>
                    </div>
                    {restante > 0 && (
                        <div className="summary-row restante">
                            <span>Restante:</span>
                            <span className="text-red">{formatMoney(restante)}</span>
                        </div>
                    )}
                    {cambio > 0 && (
                        <div className="summary-row cambio">
                            <span>Cambio:</span>
                            <span className="text-green">{formatMoney(cambio)}</span>
                        </div>
                    )}
                </div>

                {/* Estatus de pago (opcional) */}
                {showEstatusPago && (
                    <div className="payment-status-section">
                        <label className="section-label">Estatus de pago:</label>
                        <div className="status-options">
                            <button
                                className={`status-btn ${estatusPago === 'pendiente' ? 'active' : ''}`}
                                onClick={() => {
                                    setEstatusPago('pendiente');
                                    onEstatusPagoChange && onEstatusPagoChange('pendiente');
                                }}
                            >
                                <span>Pendiente</span>
                                <span className="status-key">P</span>
                            </button>
                            <button
                                className={`status-btn ${estatusPago === 'pagado' ? 'active' : ''}`}
                                onClick={() => {
                                    setEstatusPago('pagado');
                                    onEstatusPagoChange && onEstatusPagoChange('pagado');
                                }}
                            >
                                <span>Pagado</span>
                                <span className="status-key">G</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Botones */}
                <div className="payment-modal-actions">
                    <button className="btn-cancel" onClick={onClose}>
                        Cancelar (Esc)
                    </button>
                    <button
                        className="btn-confirm"
                        onClick={handleConfirm}
                        disabled={restante > 0.01}
                    >
                        <Check size={18} />
                        Confirmar (Enter)
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default PaymentModal;
